// Блок со свайпом на малых экранах и постраничным просмотром на больших
import Swiper from 'swiper';
import { cacheWindow, cacheScrollbar } from 'object-cache';

class Options {
    constructor(opts = {}) {
        this.values = opts;
    }

    get showMoreClass() {
        return this.values.showMoreClass || 'js-sNp-show-more';
    }

    get contentClass() {
        return this.values.contentClass || 'js-sNp-content';
    }

    get slideClass() {
        return this.values.slideClass || 'js-sNp-slide';
    }

    get wrapperClass() {
        return this.values.wrapperClass || 'js-sNp-slides';
    }

    get swiperInitWidth() {
        return this.values.swiperInitWidth || 767;
    }

    get canLoadMore() {
        return this.values.canLoadMore !== undefined
            ? this.values.canLoadMore
            : true;
    }
}

class ShowMoreElement {
    constructor(opts = {}) {
        this.maxLines = opts.maxLines || 4;
        this.slideSelector = opts.slideSelector;
        this.isDummy = !opts.canLoadMore;

        if (this.isDummy) {
            return;
        }

        if (!this.slideSelector) {
            throw new Error('Slide selector not found');
        }

        this.shownLines = 1;
        this.lastLineHeight = undefined;
    }

    init(opts = {}) {
        if (this.isDummy) {
            return;
        }

        const element = opts.element;
        const button = opts.button;

        if (!element || !element.length || !button || !button.length) {
            throw new Error('Show more elements not found');
        }

        this.element = element;
        this.button = button;

        this.button.on('click', this.showNextLine.bind(this));

        this._enabled = true;
    }

    set enabled(value) {
        if (this.isDummy) {
            return;
        }

        this._enabled = value;

        if (!value) {
            this.shownLines = 1;
            this.lastLineHeight = undefined;
            this.slideHeightCache = undefined;
            this.clearHeight();
        }
    }

    get enabled() {
        if (this.isDummy) {
            return false;
        }

        return this._enabled;
    }

    get slideHeight() {
        if (this.isDummy) {
            return 0;
        }

        if (this.slideHeightCache !== undefined) {
            return this.slideHeightCache;
        }

        const slide = this.element.find(`.${this.slideSelector}:first`);

        if (!slide || !slide.length) {
            this.slideHeightCache = 0;
            return 0;
        }

        this.slideHeightCache = slide.height();
        return this.slideHeightCache;
    }

    showNextLine() {
        if (this.isDummy) {
            return;
        }

        if (!this.enabled) {
            return;
        }

        if (this.shownLines >= this.maxLines) {
            return;
        }

        this.shownLines++;
        this.updateHeight(true);
    }

    onResize() {
        if (this.isDummy) {
            return;
        }

        if (!this.enabled) {
            return;
        }

        // drop height cache on resize
        this.slideHeightCache = undefined;
        const slideHeight = this.slideHeight;

        // slide height changed
        if (this.lastLineHeight !== slideHeight) {
            // make recalculate height
            this.updateHeight(false);
        }
        this.lastLineHeight = slideHeight;
    }

    updateHeight(withAnimate) {
        if (this.isDummy) {
            return;
        }

        if (!this.enabled) {
            return;
        }

        const newHeight = this.shownLines * (this.slideHeight - 1);

        if (withAnimate) {
            this.element.animate({ height: `${newHeight}` }, 500, () => {
                this.updateButtonState();
            });
        } else {
            this.element.css({
                height: newHeight,
            });
            this.updateButtonState();
        }
    }

    updateButtonState() {
        if (this.isDummy) {
            return;
        }

        if (!this.enabled || this.shownLines >= this.maxLines) {
            this.button.hide();
        } else {
            this.button.show();
        }
    }

    clearHeight() {
        if (this.isDummy) {
            return;
        }

        this.element.css({
            height: '',
        });
        this.updateButtonState();
    }
}

export default class SwipeAndPaging {
    constructor(opts = {}) {
        this.options = new Options(opts);
        this.runned = false;
        this.resizeTimeout = null;
        this.showMore = new ShowMoreElement({
            maxLines: 4,
            slideSelector: this.options.slideClass,
            canLoadMore: this.options.canLoadMore,
        });
    }

    run(element) {
        if (this.runned) {
            return;
        }

        if (!element || !element.length) {
            console.warn('No element for swiper block');
            return;
        }

        this.element = element.find(`.${this.options.contentClass}`);

        cacheWindow.$element.on('resize', this.onResize.bind(this));

        this.showMore.init({
            element: this.element.find(`.${this.options.wrapperClass}`),
            button: element.find(`.${this.options.showMoreClass}`),
        });

        this.runned = true;
        this.updateState();
    }

    cleartimeout() {
        if (this.resizeTimeout) {
            window.clearTimeout(this.resizeTimeout);
            this.resizeTimeout = null;
        }
    }

    updateState() {
        const windowWidth = cacheWindow.width + cacheScrollbar.width;

        if (windowWidth <= this.options.swiperInitWidth) {
            if (this.swiper) {
                return;
            }

            this.swiper = new Swiper(this.element.get(0), {
                slidesPerView: 'auto',
                centeredSlides: true,
                calculateHeight: true,
                slideClass: this.options.slideClass,
                wrapperClass: this.options.wrapperClass,
            });
            this.showMore.enabled = false;
        } else {
            if (!this.swiper) {
                this.showMore.onResize();
                return;
            }

            this.swiper.destroy(true, true);
            this.swiper = null;
            this.showMore.enabled = true;
            this.showMore.onResize();
        }
    }

    onResize() {
        this.cleartimeout();
        this.resizeTimeout = window.setTimeout(() => {
            this.resizeTimeout = null;
            this.updateState();
        }, 10);
    }
}
